/**
 * CLUMSY CHEF | FREE SHIPPING PROGRESS
 *
 * Compatible with Horizon-style cart rendering.
 * Uses Shopify's locale-aware Cart Ajax API.
 *
 * Displays progress toward a $50 USD threshold.
 * Does not modify the cart or shipping rates.
 */

(() => {
  "use strict";

  if (window.ClumsyShippingProgressInitialized) return;
  window.ClumsyShippingProgressInitialized = true;

  const SELECTOR = "[data-clumsy-shipping]";

  const cartRoot =
    window.Shopify?.routes?.root || "/";

  let updateTimer = null;
  let requestNumber = 0;
  let abortController = null;

  function formatMoney(amount, currency) {
    return new Intl.NumberFormat(
      document.documentElement.lang || "en-US",
      {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(amount / 100);
  }

  function hasSubscription(cart) {
    return cart.items.some((item) => {
      return Boolean(item.selling_plan_allocation);
    });
  }

  function getMessage(remaining, total, currency) {
    if (total === 0) {
      return "Your treat box starts here! FREE US shipping at $50.";
    }

    if (remaining <= 0) {
      return "Your treats are riding FREE! Shipping's on us.";
    }

    const amount = formatMoney(remaining, currency);

    if (remaining <= 500) {
      return `Only ${amount} away! So close!`;
    }

    if (remaining <= 1500) {
      return `Just ${amount} more until FREE shipping!`;
    }

    return `Add ${amount} more for FREE shipping!`;
  }

  function updateElement(element, cart) {
    const goal = Number(element.dataset.goal) || 5000;

    // Shopify supplies cart prices in currency subunits.
    const total = Number(cart.total_price) || 0;
    const remaining = Math.max(0, goal - total);
    const percent = Math.min(
      100,
      Math.max(0, (total / goal) * 100)
    );

    const currency =
      cart.currency ||
      element.dataset.currency ||
      "USD";

    const message = getMessage(
      remaining,
      total,
      currency
    );

    const messageElement = element.querySelector(
      "[data-shipping-message]"
    );

    const fill = element.querySelector(
      "[data-shipping-fill]"
    );

    const progress = element.querySelector(
      '[role="progressbar"]'
    );

    const subscriptionNote = element.querySelector(
      "[data-shipping-subscription-note]"
    );

    const announcement = element.querySelector(
      "[data-shipping-announcement]"
    );

    element.hidden = cart.item_count === 0;

    element.classList.toggle(
      "is-complete",
      remaining === 0 && cart.item_count > 0
    );

    if (messageElement && messageElement.textContent !== message) {
      messageElement.textContent = message;

      if (announcement) {
        announcement.textContent = message;
      }
    }

    if (fill) {
      fill.style.width = `${percent}%`;
    }

    if (progress) {
      progress.setAttribute(
        "aria-valuenow",
        String(Math.round(percent))
      );
    }

    if (subscriptionNote) {
      subscriptionNote.hidden = !hasSubscription(cart);
    }
  }

  async function updateAll() {
    const elements = document.querySelectorAll(SELECTOR);

    if (!elements.length) return;

    const currentRequest = ++requestNumber;

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const response = await fetch(
        `${cartRoot}cart.js`,
        {
          method: "GET",
          credentials: "same-origin",
          cache: "no-store",
          signal: abortController.signal,
          headers: {
            Accept: "application/json"
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Cart request failed: ${response.status}`
        );
      }

      const cart = await response.json();

      if (currentRequest !== requestNumber) return;

      document.querySelectorAll(SELECTOR).forEach(
        (element) => updateElement(element, cart)
      );

    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn(
          "[Clumsy Chef] Shipping progress:",
          error
        );
      }
    }
  }

  function scheduleUpdate(delay = 150) {
    clearTimeout(updateTimer);

    updateTimer = setTimeout(() => {
      updateAll();
    }, delay);
  }

  // Horizon-style cart updates.
  document.addEventListener("cart:update", () => {
    scheduleUpdate(200);
  });

  document.addEventListener("cart:refresh", () => {
    scheduleUpdate(200);
  });

  document.addEventListener("cart:change", () => {
    scheduleUpdate(200);
  });

  // Catch cart drawer section replacement or re-rendering.
  const observer = new MutationObserver((mutations) => {
    let cartChanged = false;

    for (const mutation of mutations) {
      if (mutation.type !== "childList") continue;

      // Ignore updates made inside our own progress bar.
      if (
        mutation.target instanceof Element &&
        mutation.target.closest(SELECTOR)
      ) {
        continue;
      }

      const nodes = [
        ...mutation.addedNodes,
        ...mutation.removedNodes
      ];

      for (const node of nodes) {
        if (!(node instanceof Element)) continue;

        if (
          node.matches(SELECTOR) ||
          node.querySelector(SELECTOR) ||
          node.matches(
            "cart-items-component, cart-drawer-component"
          ) ||
          node.querySelector(
            "cart-items-component, cart-drawer-component"
          )
        ) {
          cartChanged = true;
          break;
        }
      }

      if (cartChanged) break;
    }

    if (cartChanged) {
      scheduleUpdate(100);
    }
  });

  function initialize() {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    updateAll();
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  window.addEventListener("pageshow", () => {
    scheduleUpdate(0);
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      scheduleUpdate(0);
    }
  });

})();